import React, { useState, useEffect, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import AppLayout from "./layout/AppLayout";
import Login from "./pages/login";
import Signup from "./pages/signup";
import ForgotPassword from "./pages/ForgotPassword";
import PlannerPage from "./pages/PlannerPage";
import LeaveManager from "./pages/LeaveManager";
import VersionHistory from "./pages/VersionHistory";
import BlueprintManager from "./pages/BlueprintManager";
import IAScheduler from "./pages/IAScheduler";
import LectureNotes from "./pages/LectureNotes";
import FacultyAssistant from "./pages/FacultyAssistant";
import QuestionBank from "./pages/Questionbank";
import ProblemStatement from "./pages/Problemstatement";
import PlacementPrep from "./pages/PlacementPrep";
import MaintainLogs from "./pages/MaintainLogs";
import RecordManagement from "./pages/RecordManagement";
import Dashboard from "./pages/Dashboard";
import { isAuthTokenValid } from "./api";
import { AppNotifyProvider, useAppNotify } from "./components/AppNotify";
import { clearWorkspaceCache } from "./utils/workspaceIsolation";
import { applyBrandPalette } from "./constants/colors";
import { createAppTheme } from "./theme/muiTheme";

function getStoredUser() {
  try {
    // Prefer App auth key; fall back to planner cache
    const raw = localStorage.getItem("user") || localStorage.getItem("ap_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  clearWorkspaceCache({ rememberOwner: true });
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("ap_user");
}

// Wraps a page element: redirects to /login if no valid session
function Protected({ user, onLogout, children }) {
  if (!user || !isAuthTokenValid()) {
    return <Navigate to="/login" replace />;
  }
  return <AppLayout user={user} onLogout={onLogout}>{children}</AppLayout>;
}

function LoginRoute({ user, onLogin }) {
  const navigate = useNavigate();
  if (user && isAuthTokenValid()) return <Navigate to="/planner" replace />;
  return (
    <Login
      onLogin={onLogin}
      goToForgot={() => navigate("/forgot-password")}
    />
  );
}

function SignupRoute({ user, onLogin }) {
  const navigate = useNavigate();
  if (user && isAuthTokenValid()) return <Navigate to="/planner" replace />;
  return <Signup goToLogin={() => navigate("/login")} onSignup={() => navigate("/login")} />;
}

function ForgotPasswordRoute() {
  const navigate = useNavigate();
  return <ForgotPassword goToLogin={() => navigate("/login")} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppNotifyProvider>
        <AppRoutes />
      </AppNotifyProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { notify } = useAppNotify();
  const [user, setUser] = useState(() => {
    const u = getStoredUser();
    if (u && !isAuthTokenValid()) {
      clearSession();
      return null;
    }
    return u;
  });

  const theme = useMemo(() => {
    applyBrandPalette();
    return createAppTheme();
  }, []);

  const handleLogin = (u) => {
    setUser(u);
    const name = u?.name ? `, ${u.name}` : "";
    notify({ type: "success", title: "Logged in successfully", message: `Welcome back${name}.` });
  };

  const handleLogout = useCallback(() => {
    clearSession();
    setUser(null);
    notify({ type: "success", title: "Logged out", message: "You have been logged out successfully." });
  }, [notify]);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      notify({
        type: "warning",
        title: "Session expired",
        message: "Please sign in again to continue.",
      });
    };
    window.addEventListener("ap:auth-expired", onExpired);
    return () => window.removeEventListener("ap:auth-expired", onExpired);
  }, [notify]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/login" element={<LoginRoute user={user} onLogin={handleLogin} />} />
        <Route path="/signup" element={<SignupRoute user={user} onLogin={handleLogin} />} />
        <Route
          path="/forgot-password"
          element={
            user && isAuthTokenValid()
              ? <Navigate to="/planner" replace />
              : <ForgotPasswordRoute />
          }
        />

        <Route path="/planner" element={<Protected user={user} onLogout={handleLogout}><PlannerPage /></Protected>} />
        <Route path="/dashboard" element={<Protected user={user} onLogout={handleLogout}><Dashboard /></Protected>} />
        <Route path="/daily" element={<Navigate to="/planner" replace />} />
        <Route path="/leave" element={<Protected user={user} onLogout={handleLogout}><LeaveManager /></Protected>} />
        <Route path="/versions" element={<Protected user={user} onLogout={handleLogout}><VersionHistory /></Protected>} />
        <Route path="/blueprint" element={<Protected user={user} onLogout={handleLogout}><BlueprintManager /></Protected>} />
        <Route path="/ia" element={<Protected user={user} onLogout={handleLogout}><IAScheduler /></Protected>} />
        <Route path="/notes" element={<Protected user={user} onLogout={handleLogout}><LectureNotes /></Protected>} />
        <Route path="/faculty" element={<Protected user={user} onLogout={handleLogout}><FacultyAssistant /></Protected>} />
        <Route path="/qbank" element={<Protected user={user} onLogout={handleLogout}><QuestionBank /></Protected>} />
        <Route path="/problems" element={<Protected user={user} onLogout={handleLogout}><ProblemStatement /></Protected>} />
        <Route path="/placement" element={<Protected user={user} onLogout={handleLogout}><PlacementPrep /></Protected>} />
        <Route path="/logs" element={<Protected user={user} onLogout={handleLogout}><MaintainLogs /></Protected>} />
        <Route path="/records" element={<Protected user={user} onLogout={handleLogout}><RecordManagement /></Protected>} />

        <Route path="*" element={<Navigate to={user && isAuthTokenValid() ? "/planner" : "/login"} replace />} />
      </Routes>
    </ThemeProvider>
  );
}
